import { IChartBar } from "./interfaces";
import { IEventWithHours } from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBarInterfaces";
import { handleNameTranslation, translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { roundToMinutes } from "../downtimeSlideUtils";

interface LocationGroup {
    country_code: string;
    functional_location_id: number;
    functional_location_name: string;
    functional_location_total_occurrences: number;
    functional_location_total_minutes: number;
    functional_location_failure_types: {
        failure_type_id: number;
        failure_type_name: string;
        color_code: string;
        total: number;
    }[];
}

export interface LocationsChartResult {
    hasData: boolean;
    barsChartData: IChartBar[];
}

export const buildLocationsChartData = (
    downtimeEvents: IEventWithHours[] | undefined,
    displayUnit: string,
    language: string,
    countryCode: string | undefined,
): LocationsChartResult => {
    const groups: { [key: string]: LocationGroup } = {};

    if (downtimeEvents) {
        downtimeEvents
            .filter((event) => event.functional_location !== null)
            .forEach((event) => {
                const groupName = handleNameTranslation(
                    language,
                    event.functional_location.translation.translation_names,
                );
                const group = groups[groupName] || {
                    functional_location_id: event.functional_location.functional_location_id,
                    functional_location_name: groupName,
                    functional_location_total_occurrences: 0,
                    functional_location_total_minutes: 0,
                    functional_location_failure_types: [],
                };

                group.country_code = countryCode!;

                if (displayUnit === "minutes") {
                    group.functional_location_total_minutes += event.validTime;
                } else {
                    group.functional_location_total_occurrences += 1;
                }

                const existingIdx = group.functional_location_failure_types.findIndex(
                    (ft) => ft.failure_type_id === event.failure_type.failure_type_id,
                );

                if (existingIdx !== -1) {
                    group.functional_location_failure_types[existingIdx].total +=
                        displayUnit === "minutes" ? event.validTime : 1;
                } else {
                    group.functional_location_failure_types.push({
                        failure_type_id: event.failure_type.failure_type_id,
                        failure_type_name: translateDefaultEntity(
                            language,
                            event.failure_type,
                            "name",
                            DefaultTranslationFieldsInTable.FAILURE_TYPE,
                        ),
                        color_code: event.failure_type.color_code,
                        total: displayUnit === "minutes" ? event.validTime : 1,
                    });
                }

                groups[groupName] = group;
            });
    }

    const locations = Object.values(groups);
    const hasData = locations.length > 0;

    const barsChartData = locations
        .sort((a, b) =>
            displayUnit === "minutes"
                ? b.functional_location_total_minutes - a.functional_location_total_minutes
                : b.functional_location_total_occurrences - a.functional_location_total_occurrences,
        )
        .slice(0, 5)
        .reverse()
        .map((stat) => {
            const chartBar: IChartBar = {
                functionalLocation: stat.functional_location_name,
                countryCode: stat.country_code,
            };

            stat.functional_location_failure_types.forEach((item) => {
                chartBar[`${item.failure_type_name}Color`] = item.color_code;
                chartBar[`${item.failure_type_name}`] =
                    displayUnit === "minutes" ? roundToMinutes(item.total) : item.total;
            });

            return chartBar;
        });

    return { hasData, barsChartData };
};
